import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';

// Gallery images from public/images/about
const galleryImages = [
  { src: '/images/about/image1.jpeg', title: 'யாகம் & ஹோமம்' },
  { src: '/images/about/image2.jpeg', title: 'பூஜை நிகழ்வுகள்' },
  { src: '/images/about/image3.jpeg', title: 'விழா தருணங்கள்' },
  { src: '/images/about/image4.png', title: 'ஆலய வழிபாடு' },
  { src: '/images/about/image5.jpeg', title: 'சிறப்பு நிகழ்ச்சிகள்' },
];

const HomePage = () => {
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const descriptionRef = useRef(null);
  const ctaRef = useRef(null);

  useEffect(() => {
    const tl = gsap.timeline({ delay: 0.8 });

    // Staggered text reveal animation
    tl.fromTo(
      titleRef.current.children,
      { 
        opacity: 0, 
        y: 80,
        rotationX: -90,
      },
      { 
        opacity: 1, 
        y: 0,
        rotationX: 0,
        duration: 1.2,
        stagger: 0.1,
        ease: 'power4.out',
      }
    )
    .fromTo(
      subtitleRef.current,
      { opacity: 0, y: 40, filter: 'blur(10px)' },
      { opacity: 1, y: 0, filter: 'blur(0px)', duration: 1, ease: 'power3.out' },
      '-=0.6'
    )
    .fromTo(
      descriptionRef.current,
      { opacity: 0, y: 30 },
      { opacity: 0.8, y: 0, duration: 1, ease: 'power3.out' },
      '-=0.5'
    )
    .fromTo(
      ctaRef.current,
      { opacity: 0, scale: 0.9 },
      { opacity: 1, scale: 1, duration: 0.8, ease: 'back.out(1.7)' },
      '-=0.4'
    );

    return () => tl.kill();
  }, []);

  const titleText = "astorolgy";

  return (
    <>
    <motion.div
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Content - Layered above the wheel */}
      <div
        ref={heroRef}
        className="relative z-10 text-center px-4 max-w-5xl mx-auto"
        style={{ perspective: '1000px' }}
      >
        {/* Main Title with letter animation */}
        <h1
          ref={titleRef}
          className="text-4xl md:text-6xl lg:text-7xl font-display font-bold mb-6 tracking-wider"
          style={{
            color: '#d4af37',
            textShadow: '0 0 60px rgba(212, 175, 55, 0.5), 0 0 120px rgba(212, 175, 55, 0.2)',
            lineHeight: 1.3,
          }}
        >
          {titleText.split('').map((char, i) => (
            <span
              key={i}
              className="inline-block"
              style={{ 
                transformOrigin: 'bottom center',
                display: char === ' ' ? 'inline' : 'inline-block',
              }}
            >
              {char === ' ' ? '\u00A0' : char}
            </span>
          ))}
        </h1>

        {/* Subtitle */}
        <p
          ref={subtitleRef}
          className="text-2xl md:text-3xl lg:text-4xl font-body italic mb-4"
          style={{
            color: '#c0c0c0',
            textShadow: '0 0 40px rgba(192, 192, 192, 0.3)',
          }}
        >
          உங்கள் விதியை கண்டறியுங்கள்
        </p>
        
        {/* English Tagline */}
        <p
          className="text-lg md:text-xl font-body mb-8"
          style={{
            color: 'rgba(212, 175, 55, 0.7)',
          }}
        >
          Sri Selvaganapathy Jothidam 
        </p>

        {/* Description */}
        <p
          ref={descriptionRef}
          className="text-lg md:text-xl font-body max-w-2xl mx-auto mb-12 leading-relaxed"
          style={{ color: 'rgba(192, 192, 192, 0.8)' }}
        >
          25 ஆண்டுகள் அனுபவம் கொண்ட ஜோதிட நிபுணர். 
          ராசி பலன், ஜாதக கணிப்பு, திருமண பொருத்தம், தொழில் ஆலோசனை.
        </p>

        {/* CTA Button */}
        <div ref={ctaRef}>
          <CTAButton />
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 3, duration: 1 }}
        >
          <motion.div
            className="w-6 h-10 rounded-full border-2 flex justify-center pt-2"
            style={{ borderColor: 'rgba(212, 175, 55, 0.4)' }}
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <motion.div
              className="w-1.5 h-3 rounded-full"
              style={{ backgroundColor: '#d4af37' }}
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            />
          </motion.div>
        </motion.div>
      </div>

      {/* Services Section */}
      <div className="absolute bottom-0 left-0 right-0 pb-32">
        <ServicesPreview />
      </div>
    </motion.div>

    {/* Zig-Zag Gallery Section - Outside the hero container */}
    <GallerySection />
  </>
  );
};

const CTAButton = () => {
  const buttonRef = useRef(null);
  const glowRef = useRef(null);

  useEffect(() => {
    const button = buttonRef.current;
    const glow = glowRef.current;

    const handleMouseEnter = () => {
      gsap.to(glow, {
        opacity: 1,
        scale: 1.5,
        duration: 0.5,
        ease: 'power2.out',
      });
      gsap.to(button, {
        scale: 1.05,
        boxShadow: '0 0 60px rgba(212, 175, 55, 0.6), 0 0 100px rgba(212, 175, 55, 0.3)',
        duration: 0.3,
        ease: 'power2.out',
      });
    };

    const handleMouseLeave = () => {
      gsap.to(glow, {
        opacity: 0.5,
        scale: 1,
        duration: 0.5,
        ease: 'power2.out',
      });
      gsap.to(button, {
        scale: 1,
        boxShadow: '0 0 40px rgba(212, 175, 55, 0.4), 0 0 80px rgba(212, 175, 55, 0.2)',
        duration: 0.3,
        ease: 'power2.out',
      });
    };

    const handleMouseMove = (e) => {
      const rect = button.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const deltaX = (e.clientX - centerX) * 0.1;
      const deltaY = (e.clientY - centerY) * 0.1;

      gsap.to(button, {
        x: deltaX,
        y: deltaY,
        duration: 0.3,
        ease: 'power2.out',
      });
    };

    const handleMouseLeaveReset = () => {
      gsap.to(button, {
        x: 0,
        y: 0,
        duration: 0.6,
        ease: 'elastic.out(1, 0.5)',
      });
    };

    button.addEventListener('mouseenter', handleMouseEnter);
    button.addEventListener('mouseleave', handleMouseLeave);
    button.addEventListener('mousemove', handleMouseMove);
    button.addEventListener('mouseleave', handleMouseLeaveReset);

    return () => {
      button.removeEventListener('mouseenter', handleMouseEnter);
      button.removeEventListener('mouseleave', handleMouseLeave);
      button.removeEventListener('mousemove', handleMouseMove);
      button.removeEventListener('mouseleave', handleMouseLeaveReset);
    };
  }, []);

  return (
    <div className="relative inline-block">
      <div
        ref={glowRef}
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(212, 175, 55, 0.4) 0%, transparent 70%)',
          filter: 'blur(20px)',
          opacity: 0.5,
          transform: 'scale(2)',
        }}
      />
      <button
        ref={buttonRef}
        className="relative px-10 py-4 rounded-full font-display text-lg tracking-widest cursor-pointer"
        style={{
          background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(212, 175, 55, 0.1) 100%)',
          border: '1px solid rgba(212, 175, 55, 0.6)',
          color: '#d4af37',
          boxShadow: '0 0 40px rgba(212, 175, 55, 0.4), 0 0 80px rgba(212, 175, 55, 0.2)',
          backdropFilter: 'blur(10px)',
          willChange: 'transform, box-shadow',
        }}
      >
        இப்போது தொடர்பு கொள்ளுங்கள்
      </button>
    </div>
  );
};

const ServicesPreview = () => {
  const services = [
    { icon: '☉', title: 'ஜாதக கணிப்பு', subtitle: 'Birth Chart', delay: 0 },
    { icon: '☽', title: 'ராசி பலன்', subtitle: 'Rashi Palan', delay: 0.1 },
    { icon: '♀', title: 'திருமண பொருத்தம்', subtitle: 'Marriage Match', delay: 0.2 },
    { icon: '♃', title: 'தொழில் ஆலோசனை', subtitle: 'Career Guidance', delay: 0.3 },
  ];

  return (
    <div className="flex flex-wrap justify-center gap-8 px-4">
      {services.map((service, index) => (
        <motion.div
          key={index}
          className="text-center group cursor-pointer"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.5 + service.delay, duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3"
            style={{
              background: 'radial-gradient(circle, rgba(212, 175, 55, 0.1) 0%, transparent 70%)',
              border: '1px solid rgba(212, 175, 55, 0.3)',
            }}
            whileHover={{
              scale: 1.1,
              boxShadow: '0 0 40px rgba(212, 175, 55, 0.4)',
              borderColor: 'rgba(212, 175, 55, 0.6)',
            }}
            transition={{ duration: 0.3 }}
          >
            <span className="text-2xl" style={{ color: '#d4af37' }}>{service.icon}</span>
          </motion.div>
          <span
            className="text-sm font-display tracking-wider block"
            style={{ color: '#d4af37' }}
          >
            {service.title}
          </span>
          <span
            className="text-xs font-body"
            style={{ color: 'rgba(192, 192, 192, 0.6)' }}
          >
            {service.subtitle}
          </span>
        </motion.div>
      ))}
    </div>
  );
};

// Zig-Zag Gallery Component with improved contrast
const GallerySection = () => {
  return (
    <div
      className="py-24 px-4"
      style={{
        background: 'linear-gradient(180deg, rgba(10, 10, 15, 0.95) 0%, rgba(20, 15, 30, 0.98) 50%, rgba(10, 10, 15, 0.95) 100%)',
      }}
    >
      <div className="max-w-6xl mx-auto">
        <motion.h2
          className="text-3xl md:text-4xl font-display tracking-wider mb-16 text-center"
          style={{
            color: '#f4d03f',
            textShadow: '0 0 40px rgba(244, 208, 63, 0.4)',
          }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          நிகழ்வுகள் & புகைப்படங்கள்
        </motion.h2>

        <div className="space-y-20">
          {galleryImages.map((img, idx) => {
            const isLeft = idx % 2 === 0;
            return (
              <motion.div
                key={idx}
                className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center"
                initial={{ opacity: 0, x: isLeft ? -60 : 60 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7 }}
              >
                {/* Image */}
                <div className={`${isLeft ? 'md:order-1' : 'md:order-2'}`}>
                  <div
                    className="overflow-hidden rounded-2xl relative group"
                    style={{
                      border: '2px solid rgba(244, 208, 63, 0.3)',
                      boxShadow: '0 25px 80px rgba(0, 0, 0, 0.5), 0 0 40px rgba(212, 175, 55, 0.15)',
                      background: 'linear-gradient(135deg, rgba(30, 25, 40, 0.8) 0%, rgba(15, 12, 25, 0.9) 100%)',
                    }}
                  >
                    <img
                      src={img.src}
                      alt={img.title}
                      className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    {/* Overlay gradient for contrast */}
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background: 'linear-gradient(180deg, transparent 60%, rgba(10, 10, 15, 0.4) 100%)',
                      }}
                    />
                  </div>
                </div>

                {/* Title */}
                <div className={`${isLeft ? 'md:order-2' : 'md:order-1'} px-6`}>
                  <div
                    className="h-1 w-24 mb-6"
                    style={{
                      background: 'linear-gradient(90deg, #f4d03f 0%, rgba(212, 175, 55, 0.3) 100%)',
                      boxShadow: '0 0 20px rgba(244, 208, 63, 0.4)',
                    }}
                  />
                  <h3
                    className="text-2xl md:text-3xl font-display tracking-wider mb-4"
                    style={{
                      color: '#f4d03f',
                      textShadow: '0 0 30px rgba(244, 208, 63, 0.3)',
                    }}
                  >
                    {img.title}
                  </h3>
                  <p
                    className="font-body text-lg"
                    style={{ color: 'rgba(220, 220, 220, 0.85)' }}
                  >
                    பாரம்பரிய முறையில் நடத்தப்படும் யாக, ஹோம, பூஜை நிகழ்வுகள்
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HomePage;
